/**
 * Where a fork's published files live.
 *
 * Every central tool is opened as `?repo=owner/name` and has to turn that
 * into a URL it can `fetch()`. The obvious answer —
 * `owner.github.io/name/<file>` — is wrong for any fork whose GitHub Pages
 * site is served from a custom domain: Pages answers that URL with a `301`
 * to the domain, and **the redirect itself carries no
 * `Access-Control-Allow-Origin`**. CORS is enforced on every response in a
 * redirect chain, so the browser blocks the fetch before it reaches the
 * destination, even though the destination sends `ACAO: *`. Script cannot
 * read the redirect's `Location` either, so the domain has to arrive from
 * outside the fetch.
 *
 * Two ways it can, in order of reliability:
 *
 * 1. The dashboard passes the absolute URL it already knows (`?feed=`).
 * 2. The origin of the dashboard that linked here, from `document.referrer`.
 *    Browsers trim it to the bare origin cross-origin, so the path has to be
 *    guessed: project sites keep the repo name in it, user/org sites and
 *    custom domains mapped to one repo do not.
 *
 * Pure functions, DOM-free: the caller reads `document.referrer` and
 * `location.origin` and passes them in.
 */

/** Where the organizer came from, used to guess a custom Pages domain. */
export interface PagesOrigin {
  /** `document.referrer` — the dashboard that linked here, if it sent one. */
  referrer?: string;
  /** `location.origin` of the tool itself, so its own pages never qualify. */
  origin?: string;
}

/** An http(s) URL, or null for anything else — javascript:, data:, file:, junk. */
export function httpUrl(value: string | undefined | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * The candidate URLs for `file` on the fork's Pages site, most likely first:
 * the custom domain the referrer hints at (if any), then the canonical
 * `github.io` one.
 */
export function pagesUrls(repo: string, file: string, from: PagesOrigin = {}): string[] {
  const [owner, name] = repo.split("/");
  return dedupe([
    ...referrerUrls(name ?? "", file, from),
    `https://${owner}.github.io/${name}/${file}`,
  ]);
}

/**
 * Every URL worth trying for `file`, in order: the Pages candidates above,
 * then `raw.githubusercontent` on the default branch — Pages may not be
 * enabled yet (or may still be building) while the file is already
 * committed. The raw fallback is not a guarantee: a file generated by the
 * publish workflow is never on the default branch at all.
 */
export function forkFileUrls(repo: string, file: string, from: PagesOrigin = {}): string[] {
  return dedupe([
    ...pagesUrls(repo, file, from),
    `https://raw.githubusercontent.com/${repo}/HEAD/${file}`,
  ]);
}

function referrerUrls(name: string, file: string, from: PagesOrigin): string[] {
  const referrer = httpUrl(from.referrer);
  if (referrer === null) return [];
  const origin = new URL(referrer).origin;
  // Navigating inside a tool sets a referrer too; it is never a feed host.
  if (origin === from.origin) return [];
  return [`${origin}/${name}/${file}`, `${origin}/${file}`];
}

function dedupe(urls: string[]): string[] {
  return [...new Set(urls)];
}
